/**
 * Luma's card, each visitor's conversation surface (2026-10-05: it replaced the AI desk's
 * console and its answer card): what Luma says, what she heard, whether she is listening,
 * thinking or speaking, the buttons for the next step, and a text field with the world
 * keyboard for typed questions. Everyone in the room hears her; each person has their own card.
 *
 * In a headset it stands three quarters of a metre ahead, a little below eye level and to the
 * side away from Luma, tilted slightly back (2026-10-06: at stomach level the visitor had to
 * look down to read it); it comes closer and lower while typing so the keyboard beneath it is
 * at hand height. On desktop it sits at the bottom of the view.
 */
import * as THREE from 'three';
import type { CanvasPanel, Region } from '../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  font,
  panelBackground,
  text,
  wrapText,
} from '../dashboards/charts.ts';
import { FloatingPanel, type FloatingPanelSounds } from '../queries/FloatingPanel.ts';
import { drawTextField } from '../queries/textField.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { HOST_NAME } from './tour.ts';

export interface HostAction {
  id: string;
  label: string;
  primary?: boolean;
  tooltip?: string;
}

export interface HostCardContent {
  title: string;
  body: string;
  /** What the host heard the visitor say. */
  heard?: string;
  /** Listening, Thinking, Speaking, Tour 3 of 10... */
  status?: string;
  /** Whether the microphone is open (a level meter shows). */
  listening?: boolean;
  actions: HostAction[];
}

/** The card's size: small enough to sit below the line of sight (smaller since 2026-10-06). */
export const HOST_CARD = { width: 0.54, height: 0.253 };

/** Where the card sits relative to the visitor's head, per mode. */
export const CARD_POSE = {
  /**
   * Headset, reading: ahead and a little below eye level, read with a glance rather than a
   * bowed head (2026-10-06; stomach level, 0.5 m below the eyes, made the visitor look down).
   */
  reading: { ahead: 0.78, side: 0.2, below: 0.28, tilt: -0.3 },
  /** Headset, typing: raised so the keyboard beneath it is at hand height. */
  typing: { ahead: 0.55, side: 0.12, below: 0.3, tilt: -0.3 },
  /** Desktop: the bottom of the view, where the mouse reaches it. */
  desktop: { ahead: 0.85, side: 0.32, below: 0.3, tilt: -0.3 },
};

/**
 * The card goes with the visitor (2026-10-06, the user: walking away while Luma talked left
 * it behind). It stays put while they stand and read or point at it, and glides back in
 * front of them once they have walked or turned away.
 */
export const CARD_FOLLOW = {
  /** Metres walked from where the card was placed. */
  distance: 0.7,
  /** Radians turned from the way the visitor faced then. */
  angle: 0.8,
  /** How quickly it glides (per second); it settles within about half a second. */
  rate: 7,
};

const LINES_PER_PAGE = 7;

type CardRegion = { action: string };

export class HostCard extends FloatingPanel {
  content: HostCardContent | undefined;
  level = 0;
  private page = 0;
  private pages = 1;
  private pose:
    | { viewer: THREE.Vector3; facing: THREE.Vector3; avoid?: THREE.Vector3; xr: boolean }
    | undefined;
  /** Gliding after the visitor until it has caught up (`follow`). */
  private gliding = false;

  constructor(
    interactive: InteractiveSystem,
    private readonly onAction: (id: string) => void,
    private readonly onSubmit: (text: string) => void,
    sounds?: FloatingPanelSounds,
  ) {
    super(
      interactive,
      {
        width: HOST_CARD.width,
        height: HOST_CARD.height,
        pixelWidth: 760,
        pixelHeight: 356,
        name: 'host-card',
      },
      sounds,
    );
    this.panel.mesh.renderOrder = 61;
    this.panel.onHoverChange = () => undefined;
  }

  get mesh(): THREE.Object3D {
    return this.group;
  }

  /**
   * Places the card for a visitor at `viewer` looking along `facing`, on the side away from
   * the host (`avoid`).
   */
  placeFor(viewer: THREE.Vector3, facing: THREE.Vector3, avoid?: THREE.Vector3, xr = true): void {
    this.pose = { viewer: viewer.clone(), facing: facing.clone(), avoid: avoid?.clone(), xr };
    this.applyPose();
  }

  /**
   * Keeps the card with the visitor: once they have walked or turned far enough from where it
   * was placed, it glides to the same place in front of them, and keeps following until it
   * has caught up.
   */
  follow(viewer: THREE.Vector3, facing: THREE.Vector3, avoid: THREE.Vector3, dt: number): void {
    if (!this.pose || !this.isOpen) return;
    if (!this.gliding) {
      const walked = Math.hypot(viewer.x - this.pose.viewer.x, viewer.z - this.pose.viewer.z);
      const turned = Math.abs(
        Math.atan2(
          this.pose.facing.x * facing.z - this.pose.facing.z * facing.x,
          this.pose.facing.x * facing.x + this.pose.facing.z * facing.z,
        ),
      );
      if (walked < CARD_FOLLOW.distance && turned < CARD_FOLLOW.angle) return;
    }
    this.pose = {
      ...this.pose,
      viewer: viewer.clone(),
      facing: facing.clone(),
      avoid: avoid.clone(),
    };
    const from = this.group.position.clone();
    const fromQ = this.group.quaternion.clone();
    this.applyPose();
    const to = this.group.position.clone();
    const toQ = this.group.quaternion.clone();
    const k = 1 - Math.exp(-CARD_FOLLOW.rate * dt);
    this.group.position.copy(from).lerp(to, k);
    this.group.quaternion.copy(fromQ).slerp(toQ, k);
    this.gliding =
      this.group.position.distanceTo(to) > 0.01 || this.group.quaternion.angleTo(toQ) > 0.01;
  }

  private applyPose(): void {
    if (!this.pose) return;
    const { viewer, facing, avoid, xr } = this.pose;
    const p = xr ? (this.typing ? CARD_POSE.typing : CARD_POSE.reading) : CARD_POSE.desktop;
    const dir = new THREE.Vector3(facing.x, 0, facing.z);
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
    dir.normalize();
    const right = new THREE.Vector3(-dir.z, 0, dir.x);
    const hostOnRight = avoid ? right.dot(avoid.clone().sub(viewer)) > 0 : false;
    this.group.position
      .copy(viewer)
      .addScaledVector(dir, p.ahead)
      .addScaledVector(right, hostOnRight ? -p.side : p.side);
    this.group.position.y = Math.max(0.55, viewer.y - p.below);
    // Face the visitor and lean back toward their eyes.
    this.group.rotation.set(p.tilt, Math.atan2(-dir.x, -dir.z), 0, 'YXZ');
  }

  /** Shows (or updates) the card. */
  present(content: HostCardContent): void {
    const changedText = content.body !== this.content?.body;
    this.content = content;
    if (changedText) this.page = 0;
    if (!this.isOpen) this.show();
    this.panel.invalidate();
  }

  /** Updates the status line or the level meter without moving the card. */
  patch(change: Partial<HostCardContent>): void {
    if (!this.content) return;
    this.present({ ...this.content, ...change });
  }

  setLevel(level: number): void {
    if (Math.abs(level - this.level) < 0.04) return;
    this.level = level;
    this.panel.invalidate();
  }

  /** Brings the keyboard out for a typed question. */
  startTyping(): void {
    this.field.clear();
    this.setTyping(true);
    this.applyPose();
  }

  override setTyping(on: boolean): void {
    const was = this.typing;
    super.setTyping(on);
    if (was !== on) this.applyPose();
  }

  /** Dev hook: world position of a key on the card's keyboard. */
  keyWorld(label: string): [number, number, number] | undefined {
    const p = this.keyboard.keyWorld(label);
    return p ? [p.x, p.y, p.z] : undefined;
  }

  /** Dev hook: the action ids on the card now. */
  actionIds(): string[] {
    return this.typing ? ['ask', 'cancel'] : (this.content?.actions.map((a) => a.id) ?? []);
  }

  /** Presses an action by id (dev hooks): the card's current actions, not drawn regions. */
  press(id: string): boolean {
    if (!this.actionIds().includes(id)) return false;
    this.act(id);
    return true;
  }

  protected onEnter(): void {
    this.submit();
  }

  protected onEscape(): void {
    if (this.typing) this.setTyping(false);
    else this.onAction('close');
  }

  private submit(): void {
    const text = this.field.trimmed;
    if (!text) return;
    this.field.clear();
    this.setTyping(false);
    this.onSubmit(text);
  }

  private act(id: string): void {
    if (id === 'page-prev') this.turn(-1);
    else if (id === 'page-next') this.turn(1);
    else if (id === 'ask') this.submit();
    else if (id === 'cancel') this.setTyping(false);
    else if (id === 'field') this.setTyping(true);
    else this.onAction(id);
  }

  protected select(region: Region): void {
    const id = (region.data as CardRegion | undefined)?.action;
    if (id) {
      this.sounds?.click();
      this.act(id);
    }
  }

  private turn(by: number): void {
    this.page = Math.min(this.pages - 1, Math.max(0, this.page + by));
    this.panel.invalidate();
  }

  protected draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    const c = this.content;
    panelBackground(ctx, W, H, 18);
    if (!c) return;
    // Header: who is speaking and what the host is doing.
    ctx.fillStyle = PALETTE.teal;
    ctx.beginPath();
    ctx.arc(30, 30, 8, 0, Math.PI * 2);
    ctx.fill();
    text(ctx, `${HOST_NAME.toUpperCase()} · YOUR HOST`, 46, 36, {
      size: 11,
      weight: 700,
      colour: PALETTE.accentText,
    });
    const status = this.typing ? 'Typing' : c.status;
    if (status) {
      ctx.font = font(10, 700);
      const w = ctx.measureText(status).width + 26;
      const r = { x: W - 20 - w, y: 16, w, h: 28 };
      fillRoundRect(ctx, r, 14, c.listening ? PALETTE.teal : PALETTE.raised);
      text(ctx, status, r.x + w / 2, r.y + 19, {
        size: 10,
        weight: 700,
        align: 'center',
        colour: c.listening ? PALETTE.onAccent : PALETTE.text,
      });
    }
    text(ctx, c.title, 22, 80, { size: 16, weight: 600, display: true, maxWidth: W - 44 });
    let y = 96;
    if (c.listening) {
      fillRoundRect(ctx, { x: 22, y, w: W - 44, h: 8 }, 4, PALETTE.track);
      fillRoundRect(
        ctx,
        { x: 22, y, w: Math.max(8, (W - 44) * this.level), h: 8 },
        4,
        PALETTE.teal,
      );
      y += 14;
    }
    if (c.heard) {
      text(ctx, `You: “${c.heard}”`, 22, y + 22, {
        size: 11,
        colour: PALETTE.muted,
        maxWidth: W - 44,
      });
      y += 28;
    }
    // The body, paged; it gives way to the text field while typing.
    const bottom = this.typing ? H - 130 : H - 70;
    const perPage = Math.max(2, Math.min(LINES_PER_PAGE, Math.floor((bottom - y - 10) / 25)));
    ctx.font = font(13, 500);
    const lines = wrapText(ctx, c.body, W - 64);
    this.pages = Math.max(1, Math.ceil(lines.length / perPage));
    this.page = Math.min(this.page, this.pages - 1);
    lines
      .slice(this.page * perPage, (this.page + 1) * perPage)
      .forEach((l, i) => text(ctx, l, 22, y + 30 + i * 25, { size: 13, colour: PALETTE.textSoft }));
    if (this.pages > 1) {
      const prev = chip(ctx, '▲', W - 96, y + 4, { h: 26, hovered: panel.isHovered('page-prev') });
      const next = chip(ctx, '▼', W - 52, y + 4, { h: 26, hovered: panel.isHovered('page-next') });
      panel.region<CardRegion>({ id: 'page-prev', ...prev, data: { action: 'page-prev' } });
      panel.region<CardRegion>({ id: 'page-next', ...next, data: { action: 'page-next' } });
    }
    // Typing: the question field above the buttons.
    if (this.typing) {
      const r = { x: 22, y: H - 118, w: W - 44, h: 46 };
      drawTextField(ctx, r, this.field, {
        placeholder: `Type a question for ${HOST_NAME}…`,
        active: true,
        size: 13,
      });
      panel.region<CardRegion>({ id: 'field', ...r, data: { action: 'field' } });
    }
    const actions: HostAction[] = this.typing
      ? [
          { id: 'ask', label: 'Ask', primary: true },
          { id: 'cancel', label: 'Cancel' },
        ]
      : c.actions;
    let x = 22;
    for (const a of actions) {
      ctx.font = font(12, 600);
      const id = `action:${a.id}`;
      const r = chip(ctx, a.label, x, H - 56, {
        active: a.primary,
        hovered: panel.isHovered(id),
        h: 38,
        size: 12,
      });
      panel.region<CardRegion>({
        id,
        ...r,
        data: { action: a.id },
        tooltip: a.tooltip ? [a.label, a.tooltip] : undefined,
      });
      x += r.w + 8;
      if (x > W - 40) break;
    }
  }
}
